/**
 * Conversation pane component displaying chat history
 */

import React, { useEffect, useRef } from 'react';
import { AgeProfile } from '../config/ageProfiles.config';
import { VoiceLiveConversationState } from '../types/voiceLive.types';
import { ConversationMessage } from '../types/conversation.types';
import { UserIcon, AssistantIcon, ChatIcon, ClockIcon, CloseIcon } from './SvgConsolidated/SvgIcons';

interface ConversationPaneProps {
  messages: ConversationMessage[];
  ageProfile: AgeProfile;
  conversationState: VoiceLiveConversationState;
  onNewConversation: () => void;
  conversationId?: string; // For persona tracking
  isFloating?: boolean;
  isVisible?: boolean;
  onToggleVisibility?: () => void;
  hideEmptyState?: boolean; // Hide "Ready to Chat!" during reading mode
}

export default function ConversationPane({ 
  messages, 
  ageProfile, 
  conversationState, 
  onNewConversation,
  conversationId,
  isFloating = false,
  isVisible = true,
  onToggleVisibility,
  hideEmptyState = false
}: ConversationPaneProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to top when new messages arrive (keep recent messages visible at top)
  useEffect(() => {
    if (scrollRef.current && messages.length > 0) {
      // Use double requestAnimationFrame to ensure DOM has fully updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            // Scroll to top to keep most recent messages visible
            scrollRef.current.scrollTop = 0;
          }
        });
      });
    }
  }, [messages]);

  // Also scroll on conversation state changes (when AI starts speaking)
  useEffect(() => {
    if (conversationState === 'speaking' && scrollRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }
        });
      });
    }
  }, [conversationState]);

  // Force scroll to top after a short delay for any dynamic content changes
  useEffect(() => {
    const scrollToTop = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    };

    // Scroll after messages change
    if (messages.length > 0) {
      const timeoutId = setTimeout(scrollToTop, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [messages.length]);

  const formatTimestamp = (timestamp: Date): string => {
    return timestamp.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getMessageIcon = (type: 'user' | 'assistant') => {
    if (type === 'user') {
      return (
        <div className="w-8 h-8 flex items-center justify-center border-2 border-blue-500 bg-white">
          <UserIcon className="w-4 h-4 text-blue-500" />
        </div>
      );
    } else {
      return (
        <div className="w-8 h-8 flex items-center justify-center border-2 border-gray-500 bg-white">
          <AssistantIcon className="w-4 h-4 text-gray-500" />
        </div>
      );
    }
  };

  const getEmptyStateContent = () => {
    return (
      <div className="text-center py-12">
        <div className="mb-6">
          {/* Simple chat icon */}
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center border-2 border-gray-300 bg-white">
            <ChatIcon className="w-8 h-8 text-gray-500" />
          </div>
          <h3 className="text-lg font-bold mb-3 text-gray-700">
            Ready to Chat!
          </h3>
          <p className="text-sm mb-4 text-gray-600">
            Use the microphone button to start talking with me!
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className={`flex flex-col h-full ${isFloating ? 'floating-conversation-panel' : ''}`}>
      {/* Floating Panel Header with Toggle */}
      {isFloating && (
        <div className="flex items-center justify-between p-3 bg-white border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 border border-gray-300 bg-white flex items-center justify-center">
              <ChatIcon className="w-4 h-4 text-gray-500" />
            </div>
            <span className="text-sm font-semibold text-gray-700">Chat</span>
            {messages.length > 0 && (
              <span className="text-xs bg-gray-100 border border-gray-300 px-2 py-1 text-gray-600">
                {messages.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleVisibility}
              className="p-1 hover:bg-gray-100 transition-colors text-gray-500"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Header for non-floating mode */}
      {!isFloating && (
        <div className="flex justify-between items-center mb-4">
          <h2 className={`${ageProfile.theme.typography.headingSize} font-semibold text-gray-800`}>
            Conversation
          </h2>
          <div className="flex gap-2">
            {messages.length > 0 && (
              <button
                onClick={onNewConversation}
                className={`${ageProfile.theme.spacing.buttonPadding} ${ageProfile.theme.borderRadius} text-sm font-medium text-white transition-colors`}
                style={{ backgroundColor: ageProfile.theme.colors.secondary }}
              >
                {ageProfile.content.buttonLabels.clearHistory}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Messages Container */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-4 p-4 bg-white custom-scrollbar"
        style={{ maxHeight: '400px', minHeight: '200px' }}
      >
        {messages.length === 0 ? (
          hideEmptyState ? null : getEmptyStateContent()
        ) : (
          <>
            {/* Show messages in reverse chronological order (newest first, oldest last) */}
            {[...messages].reverse().map((message, index) => (
              <div 
                key={message.id}
                className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.type === 'assistant' && (
                  <div className="flex-shrink-0">
                    {getMessageIcon('assistant')}
                  </div>
                )}

                <div 
                  className={`max-w-xs lg:max-w-md px-3 py-2 border-2 bg-white
                    ${message.type === 'user' 
                      ? 'border-blue-500' 
                      : 'border-gray-500'
                    }`}
                >
                  <p className="text-sm leading-relaxed text-gray-800">
                    {message.content}
                  </p>
                  <div className="text-xs mt-1 flex items-center gap-1 text-gray-500">
                    <ClockIcon className="w-3 h-3 opacity-75" />
                    {formatTimestamp(message.timestamp)}
                  </div>
                </div>

                {message.type === 'user' && (
                  <div className="flex-shrink-0">
                    {getMessageIcon('user')}
                  </div>
                )}
              </div>
            ))}
            

          </>
        )}

        {/* Conversation State Indicator */}
        {conversationState !== 'idle' && (
          <div className="flex justify-center mb-4">
            <div className="px-4 py-2 border border-gray-300 bg-white flex items-center gap-2">
              {conversationState === 'listening' && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 animate-pulse"></div>
                  <span className="text-sm text-gray-700">Listening...</span>
                </div>
              )}
              {conversationState === 'processing' && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 animate-spin"></div>
                  <span className="text-sm text-gray-700">Processing...</span>
                </div>
              )}
              {conversationState === 'speaking' && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 animate-bounce"></div>
                  <span className="text-sm text-gray-700">AI is speaking...</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Floating Panel Actions */}
        {isFloating && messages.length > 0 && (
          <div className="p-3 bg-white border-t border-gray-200">
            <button
              onClick={onNewConversation}
              className="w-full py-2 px-4 border border-gray-400 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Clear Chat
            </button>
          </div>
        )}
      </div>
    </div>
  );
}